// CompanyCreation wizard — step 1: company logo, name, role, business email.

import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Icon, Input } from '@/components/common';
import type { CompanyCreationData } from './company-creation-types';
import type { FormErrors } from './validation';

interface StepProps {
  data: CompanyCreationData;
  onChange: (patch: Partial<CompanyCreationData>) => void;
  errors: FormErrors;
}

const CompanyCreationStepInfo = ({ data, onChange, errors }: StepProps) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange({ logo: URL.createObjectURL(file), logoName: file.name, logoFile: file });
  };

  return (
    <div className='company-creation__fields'>
      <div className='company-creation__field'>
        <span className='company-creation__label'>{t('onb_company_logo')}</span>
        <button
          type='button'
          className='company-creation__upload'
          onClick={() => fileRef.current?.click()}
        >
          <span className='company-creation__upload-icon'>
            {data.logo ? (
              <img src={data.logo} alt='' className='company-creation__upload-preview' />
            ) : (
              <Icon name='image' size={22} aria-hidden='true' />
            )}
          </span>
          <span className='company-creation__upload-text'>
            <span className='company-creation__upload-title'>
              {data.logoName || t('onb_upload_logo')}
            </span>
            <span className='company-creation__upload-hint'>{t('onb_upload_hint')}</span>
          </span>
        </button>
        <input
          ref={fileRef}
          type='file'
          accept='image/*'
          className='company-creation__file'
          onChange={handleFile}
        />
      </div>

      <Field
        label={t('onb_company_name')}
        htmlFor='reg-company-name'
        error={errors.companyName ? t(errors.companyName) : undefined}
      >
        <Input
          id='reg-company-name'
          placeholder={t('onb_company_name_ph')}
          value={data.companyName}
          onChange={(e) => onChange({ companyName: e.target.value })}
          error={!!errors.companyName}
        />
      </Field>

      <Field label={t('onb_role_question')} htmlFor='reg-role'>
        <Input
          id='reg-role'
          placeholder={t('onb_role_ph')}
          value={data.role}
          onChange={(e) => onChange({ role: e.target.value })}
        />
      </Field>

      <Field
        label={t('onb_business_email')}
        htmlFor='reg-email'
        error={errors.email ? t(errors.email) : undefined}
      >
        <Input
          id='reg-email'
          type='email'
          iconEnd='mail'
          placeholder={t('onb_business_email_ph')}
          value={data.email}
          onChange={(e) => onChange({ email: e.target.value })}
          error={!!errors.email}
        />
      </Field>
    </div>
  );
};

export default CompanyCreationStepInfo;
